/**
 * @fileoverview The Seed vault card: a photo of seed packets from any door but a bed's own two (B137, design rev 5) is
 * seeds, not plants. Instead of "What is this?", "Seed packets" (or "3 seed packets") and the packets read off it, each a
 * row with a tick, then one line and one button, "Save 3 to Seed vault".
 *
 * Each packet is a row (PacketRow through lib/usePacketRows in its keep mode, the same rows as today's card): tick ·
 * its own packet cut out of the photo · its name · ›. Every row is ticked unless the server says its seed is already in
 * her Seed vault ("Already in your Seed vault", unticked; ticking it keeps a second packet), a packet Grow could not
 * place included (it is kept as printed). The rest of the row opens the row's change dialog (fix the name, "Remove this
 * packet"). The line under the rows says what a save does ("Kept as seeds in your Seed vault. Nothing is planted until
 * you plan it there."); with none ticked it asks her to tick the packets, and the button waits. The button counts the
 * ticked rows, live. Cancel, left of it, keeps the photo (the sheet's close, or on the Photos page the card closed back
 * to its photo): it removes nothing.
 *
 * Saving sends only the ticked rows (POST /api/packets/keep, keepPackets): the server makes the Seed vault if she has
 * none, keeps one seed for each, and takes the photo off the tray; no plant, no bed, no stage, no Free room. A card for
 * a bed's own door is PacketPile's other body; which of the two is drawn is PacketPile's one rule.
 *
 * Where: PacketPile (PileCard: the Photos page and the "Save" sheet).
 * API: POST /packets/keep (keepPackets); the rows' (the plant catalogue, the suggestion's POST /plants/identify and POST
 * /plants/own).
 * Words (en/es): packets.keep.* (title, titleCount, line, save, saveOne, none, inVault), packets.saveFailed; the rows' and
 * the dialog's own.
 * A failed save says why (lib/errorMessage): the server's refusal, or for a failure on our side (a 500, no connection) the
 * standard sentence, reported to Sentry, with the support address as a link; else packets.saveFailed.
 *
 * @module PacketKeep
 */

import React from 'react';
import { Button } from '@/shared/components/ui/button';
import { AddCancelRow } from './AddCancelRow';
import Spinner from '@/shared/components/ui/Spinner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { keepPackets, type KeepResult, type PacketScan } from '@/shared/api/packets';
import type { BedName } from '@/shared/api/locations';
import { usePacketRows } from '@/features/packets/lib/usePacketRows';
import { keepItem } from '@/features/packets/lib/packetRows';

/** What the Seed vault card is given. */
interface PacketKeepProps {
  /** What the packet reader read. */
  scan: PacketScan;
  /** The live beds (a row's dialog needs the list). */
  locations: BedName[];
  /** True while the card is busy elsewhere (its "Ask about it as a plant" being sent). */
  busy: boolean;
  /** For the fields' ids. */
  idBase: string;
  /** The packets were kept: what the server made. */
  onKept: (result: KeepResult) => void;
  /** Cancel, beside the Save button: keeps the photo. */
  onCancel?: () => void;
}

/**
 * The Seed vault card: the packets read, each a row to tick or change, and the button that keeps the ticked ones.
 *
 * @param props - PacketKeepProps.
 */
export const PacketKeep: React.FC<PacketKeepProps> = ({ scan, locations, busy, idBase, onKept, onCancel }) => {
  const { reactTranslate: t } = useTranslation();
  const [saving, setSaving] = React.useState(false);
  // Why the last save failed (lib/errorMessage's sentence), or null.
  const [failed, setFailed] = React.useState<string | null>(null);
  const off = saving || busy;
  const { index, shown, kept, list, dialog } = usePacketRows({ scan, sharedBed: '', beds: locations, disabled: off, idBase, keep: true });

  /** Keeps the ticked packets as seeds; the photo leaves the tray with them. */
  const save = async (): Promise<void> => {
    if (kept.length === 0) return;
    setSaving(true);
    setFailed(null);
    try {
      onKept(await keepPackets(scan, kept.map((row) => keepItem(index, row))));
    } catch (error) {
      setFailed(errorMessage(error, t('packets.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <h3 className="font-semibold font-body">
        {t(shown.length === 1 ? 'packets.keep.title' : 'packets.keep.titleCount', { count: shown.length })}
      </h3>

      {list}

      {failed && <p className="text-sm font-medium text-destructive" role="alert"><FailureText text={failed} /></p>}
      <p className="text-center text-xs text-muted-foreground" role="status">
        {kept.length === 0 ? t('packets.keep.none') : t('packets.keep.line')}
      </p>
      <AddCancelRow onCancel={onCancel} disabled={off}>
        <Button className="w-full" size="lg" onClick={() => void save()} disabled={off || kept.length === 0}>
          {saving ? <Spinner size={16} color="currentColor" /> : kept.length === 1 ? t('packets.keep.saveOne') : t('packets.keep.save', { count: kept.length })}
        </Button>
      </AddCancelRow>

      {dialog}
    </>
  );
};
